<template>
  <div>
    <IHTechnicalPlaceSelect
      v-if="siteId != null"
      v-model:technical-category-id="event.technicalCategoryId"
      v-model:technical-place-id="event.technicalPlaceId"
      :site-id="siteId"
      :disabled="disableEvent"
    />
    <ProgressSpinner v-else-if="boardLoading" class="align-self-center" />
    <Message v-else>
      {{ t("components.issueModal.boardMissing") }}
    </Message>

    <!-- EISENHOWER-PRIORITY -->
    <EventActionFieldRowComponent
      v-if="showEisenhowerPriority === true"
      :label="t('components.issueModal.eisenhowerPriority')"
    >
      <InputRow v-slot="{ field: inputField }" name="eisenhowerPriority">
        <EisenhowerSelector
          v-bind="inputField"
          v-model="event.eisenhowerPriority"
          :disabled="disableEvent"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.area')">
      <InputRow v-slot="{ field: inputField, validation: { valid } }" name="flags">
        <SearchSelect
          v-bind="inputField"
          v-model="eventFlags"
          :mode="SelectMode.FLAG"
          class="w-100"
          :required="false"
          :filters="{
            type: flagTypes,
            siteIds: siteId,
          }"
          :exclude="excludeFromSearch"
          :multiple="true"
          :disabled="disableEvent"
          :state="valid"
          @update:model-value="$emit('flagSelected', $event)"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.maintenanceDescription')">
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        v-model="event.description"
        name="description"
        required
        :rules="[minLength(1)]"
      >
        <InputTextArea
          v-bind="inputField"
          v-model="event.description"
          class="w-100"
          auto-resize
          rows="4"
          data-testid="description"
          :state="disableEvent ? null : valid"
          :disabled="disableEvent"
          autofocus
          @keydown.enter="handleEnter"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <hr />

    <EventActionFieldRowComponent :label="t('components.issueModal.repTime')">
      <GridRow>
        <GridCol cols="9" class="pr-0">
          <InputRow
            v-slot="{ validation: { valid } }"
            :model-value="repTime"
            name="repTime"
            required
          >
            <DatePickerComponentNew
              v-model="repTime"
              :state="disableEvent ? undefined : valid"
              :clearable="false"
              data-testid="repTime"
              :range="true"
              :disabled="disableEvent"
            />
          </InputRow>
        </GridCol>
        <GridCol>
          <InputGroup>
            <InputText v-model="duration" class="w-100 text-right" disabled></InputText>
            <InputGroupAddon class="p-0">
              {{ t("license.ih.ih.hour") }}
            </InputGroupAddon>
          </InputGroup>
        </GridCol>
      </GridRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.plannedTime')">
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        v-model.number="event.plannedTime"
        name="plannedTime"
        :rules="[minValue(0)]"
      >
        <InputGroup>
          <LISAInputNumber
            v-bind="inputField"
            v-model="event.plannedTime"
            class="w-100"
            type="number"
            :min="0"
            data-testid="plannedTime"
            :class="disableEvent ? undefined : valid ? 'p-valid' : 'p-invalid'"
            :disabled="disableEvent"
          />
          <InputGroupAddon class="p-0">
            {{ t("license.ih.ih.hour") }}
          </InputGroupAddon>
        </InputGroup>
      </InputRow>
    </EventActionFieldRowComponent>
    <hr class="mt-0" />

    <EventActionFieldRowComponent :label="t('components.issueModal.internalResources')">
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        v-model="event.internalResourcesIds"
        name="internalResourcesIds"
        required
        :rules="[minItems(1)]"
      >
        <SearchSelect
          v-bind="inputField"
          v-model="event.internalResourcesIds"
          :mode="SelectMode.USER"
          class="w-100"
          data-testid="internRessource"
          :multiple="true"
          :state="disableEvent ? undefined : valid"
          label="preferredName"
          :filters="
            siteId == null
              ? undefined
              : {
                  hasRightsOnSite: {
                    siteIds: [siteId],
                    rights: SQDC_ASSING_MINIMUM_RIGHTS,
                  },
                }
          "
          :disabled="disableEvent"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.equipments')">
      <InputRow v-slot="{ field: inputField }" v-model="event.equipmentsIds" name="equipmentsIds">
        <SearchSelect
          v-bind="inputField"
          v-model="event.equipmentsIds"
          :mode="SelectMode.IHEQUIPMENT"
          :state="undefined"
          data-testId="equipment"
          :filters="{
            siteIds: siteId,
          }"
          class="w-100"
          :multiple="true"
          :disabled="disableEvent"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <slot name="trailing"></slot>
  </div>
</template>

<script setup lang="ts">
import { minItems, minLength, minValue } from "@/modules/formValidation";
import InputRow from "@/components/shared/form/InputRow.vue";
import { computed, type PropType } from "vue";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import InputTextArea from "primevue/textarea";
import InputText from "primevue/inputtext";
import { FlagTypes } from "@/graphql";
import DatePickerComponentNew from "@/components/shared/DatePickerComponentNew.vue";
import injectAsserted from "@/functions/assert/injectAsserted";
import { DisableEventInjectionKey } from "@/components/event-action/issue/IssueTypes";
import IHTechnicalPlaceSelect from "./IHTechnicalPlaceSelect.vue";
import GridRow from "@/components/shared/utility/GridRow.vue";
import GridCol from "@/components/shared/utility/GridCol.vue";
import EventActionFieldRowComponent from "@/components/event-action/EventActionFieldRowComponent.vue";
import type { IHMaintenanceEditType } from "@/components/event-action/event/modal/EventEditType";
import LISAInputNumber from "@/components/primevueWrapper/LISAInputNumber.vue";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import EisenhowerSelector from "@/components/event-action/event/eisenhowerMatrix/EisenhowerSelector.vue";
import { useProvidedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import ProgressSpinner from "primevue/progressspinner";
import Message from "primevue/message";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import { SQDC_ASSING_MINIMUM_RIGHTS } from "@/components/event-action/task/helper/SQDCHelper";
import { useSQDCGlobalSettings } from "@/modules/sqdc/helper/useSQDCGlobalSettings";

enum AbstractEventModalComponentFields {
  description = "description",
  date = "date",
  owner = "owner",
  flags = "flags",
}

const props = defineProps({
  event: {
    type: Object as PropType<IHMaintenanceEditType>,
    required: true,
  },
  flagTypes: {
    type: Array as PropType<FlagTypes[]>,
    required: true,
  },
  excludeFromSearch: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  disable: {
    type: Object as PropType<Record<AbstractEventModalComponentFields, boolean>>,
    default: () => ({
      description: false,
      owner: false,
      date: false,
      flags: false,
    }),
  },
});
const emit = defineEmits(["flagSelected", "save"]);
const i18n = useTranslation();
const disableEvent = injectAsserted(DisableEventInjectionKey);
const eventFlags = computed<number[]>({
  get() {
    return props.event.flags.map((flag) => flag.id);
  },
  set(value: number[]) {
    props.event.flags = value.map((id) => ({ id }));
  },
});

const { siteId, boardLoading } = useProvidedBoardContext();

/**
 * make repTime a computed property to convert it to a date array
 */
const repTime = computed<[DateTime | undefined, DateTime | undefined]>({
  get() {
    if (props.event.repTime.length <= 0 || props.event.repTime.some((el) => el == null)) {
      return [undefined, undefined];
    }
    return [DateTime.fromISO(props.event.repTime[0]), DateTime.fromISO(props.event.repTime[1])];
  },
  set(value) {
    props.event.repTime = value ? value.map((el) => (el ?? DateTime.now()).toISO()) : [];
  },
});

/**
 * duration is computed from repTime
 */
const duration = computed(() => {
  if (!repTime.value || repTime.value.some((el) => el == null)) {
    return "0";
  }
  return i18n.n(
    Number(repTime.value[1]!.diff(repTime.value[0]!, "hours").toObject().hours?.toFixed(2)),
  );
});

function handleEnter(e: KeyboardEvent) {
  if (e.ctrlKey || e.metaKey) {
    emit("save", e.shiftKey);
  }
}

const { showEisenhowerPriority } = useSQDCGlobalSettings();

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
